/**
 * Business hours, after-hours routing, and the callee's TCPA calling window.
 * Clocks use Intl so the result does not depend on the host timezone.
 */
import { normalizePhoneNumber } from './phone.mjs';

const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const WEEKDAY = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
const TCPA_START_MIN = 8 * 60;
const TCPA_END_MIN = 21 * 60;

const STATE_TZ = {
  AL: 'America/Chicago', AK: 'America/Anchorage', AZ: 'America/Phoenix', AR: 'America/Chicago',
  CA: 'America/Los_Angeles', CO: 'America/Denver', CT: 'America/New_York', DC: 'America/New_York',
  DE: 'America/New_York', FL: 'America/New_York', GA: 'America/New_York', HI: 'Pacific/Honolulu',
  IA: 'America/Chicago', ID: 'America/Boise', IL: 'America/Chicago', IN: 'America/Indiana/Indianapolis',
  KS: 'America/Chicago', KY: 'America/New_York', LA: 'America/Chicago', MA: 'America/New_York',
  MD: 'America/New_York', ME: 'America/New_York', MI: 'America/Detroit', MN: 'America/Chicago',
  MO: 'America/Chicago', MS: 'America/Chicago', MT: 'America/Denver', NC: 'America/New_York',
  ND: 'America/Chicago', NE: 'America/Chicago', NH: 'America/New_York', NJ: 'America/New_York',
  NM: 'America/Denver', NV: 'America/Los_Angeles', NY: 'America/New_York', OH: 'America/New_York',
  OK: 'America/Chicago', OR: 'America/Los_Angeles', PA: 'America/New_York', RI: 'America/New_York',
  SC: 'America/New_York', SD: 'America/Chicago', TN: 'America/Chicago', TX: 'America/Chicago',
  UT: 'America/Denver', VA: 'America/New_York', VT: 'America/New_York', WA: 'America/Los_Angeles',
  WI: 'America/Chicago', WV: 'America/New_York', WY: 'America/Denver'
};

const STATE_NAMES = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR', california: 'CA', colorado: 'CO',
  connecticut: 'CT', delaware: 'DE', 'district of columbia': 'DC', florida: 'FL', georgia: 'GA',
  hawaii: 'HI', idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS', kentucky: 'KY',
  louisiana: 'LA', maine: 'ME', maryland: 'MD', massachusetts: 'MA', michigan: 'MI', minnesota: 'MN',
  mississippi: 'MS', missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV', 'new hampshire': 'NH',
  'new jersey': 'NJ', 'new mexico': 'NM', 'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND',
  ohio: 'OH', oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'rhode island': 'RI', 'south carolina': 'SC',
  'south dakota': 'SD', tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA',
  washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY'
};

function npaZone(ids, zone, into) {
  for (const id of ids) into.set(String(id), zone);
}

const NPA_TZ = new Map();
npaZone([
  201, 202, 203, 207, 212, 215, 216, 217, 219, 229, 231, 234, 239, 240, 248, 252, 267, 269, 272,
  276, 301, 302, 304, 305, 315, 321, 330, 332, 336, 339, 347, 351, 352, 380, 386, 401, 404, 407,
  410, 412, 413, 419, 434, 440, 443, 445, 470, 475, 478, 484, 508, 513, 516, 517, 518, 540, 551,
  561, 567, 570, 571, 585, 586, 603, 607, 609, 610, 614, 616, 617, 631, 646, 667, 678, 680, 689,
  703, 704, 706, 716, 717, 718, 724, 727, 732, 734, 740, 754, 757, 762, 770, 772, 774, 781, 786,
  802, 803, 804, 813, 828, 835, 843, 845, 848, 854, 856, 857, 860, 862, 863, 864, 878, 904, 908,
  910, 912, 914, 917, 919, 929, 934, 937, 941, 947, 954, 959, 973, 978, 980, 984
], 'America/New_York', NPA_TZ);
npaZone([
  205, 210, 214, 217, 218, 224, 225, 228, 251, 254, 256, 262, 270, 281, 309, 312, 314, 316, 318,
  319, 320, 325, 331, 334, 337, 346, 361, 364, 402, 405, 409, 414, 417, 430, 432, 448,
  469, 479, 501, 502, 504, 507, 512, 515, 531, 534, 539, 563, 573, 580, 601, 605, 608, 612,
  615, 618, 620, 629, 630, 636, 641, 651, 660, 662, 682, 708, 712, 713, 715, 731,
  737, 763, 769, 773, 779, 785, 806, 815, 816, 817, 830, 832, 847, 850, 870, 872, 901, 903, 913,
  915, 918, 920, 930, 931, 936, 940, 945, 952, 956, 972, 979, 985
], 'America/Chicago', NPA_TZ);
npaZone([
  208, 303, 307, 385, 406, 435, 480, 505, 520, 575, 602, 623, 719, 720, 801, 928, 970, 986
], 'America/Denver', NPA_TZ);
npaZone([
  206, 209, 213, 253, 279, 310, 323, 341, 360, 408, 415, 424, 425, 442, 458, 503, 509, 510,
  530, 541, 559, 562, 564, 619, 626, 628, 650, 657, 661, 669, 702, 707, 714, 725, 747, 760, 775,
  805, 818, 820, 831, 858, 909, 916, 925, 949, 951, 971
], 'America/Los_Angeles', NPA_TZ);
npaZone([907], 'America/Anchorage', NPA_TZ);
npaZone([808], 'Pacific/Honolulu', NPA_TZ);
NPA_TZ.set('423', 'America/New_York');
NPA_TZ.set('602', 'America/Phoenix');
NPA_TZ.set('480', 'America/Phoenix');
NPA_TZ.set('520', 'America/Phoenix');
NPA_TZ.set('623', 'America/Phoenix');
NPA_TZ.set('928', 'America/Phoenix');

function parseTime(str) {
  if (!str || typeof str !== 'string') return null;
  const m = str.trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  if (h < 0 || h > 23 || min < 0 || min > 59) return null;
  return h * 60 + min;
}

/** Wall clock in an IANA zone. hour 24 (some Intl builds) is midnight. */
export function zonedClock(date, timeZone) {
  const zone = (timeZone && String(timeZone).trim()) || 'UTC';
  let fmt;
  try {
    fmt = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23'
    });
  } catch (e) {
    if (zone !== 'UTC') return zonedClock(date, 'UTC');
    throw e;
  }
  const parts = {};
  for (const p of fmt.formatToParts(date)) {
    if (p.type !== 'literal') parts[p.type] = p.value;
  }
  let hour = parseInt(parts.hour, 10);
  if (hour === 24) hour = 0;
  const minute = parseInt(parts.minute, 10) || 0;
  return {
    weekday: WEEKDAY[parts.weekday] ?? 0,
    hour,
    minute,
    minutesSinceMidnight: hour * 60 + minute,
    dateStr: `${parts.year}-${parts.month}-${parts.day}`
  };
}

/** String dates stay YYYY-MM-DD. Object entries use `date` (or `day`) so a closed date can keep a name. */
function holidayDate(entry) {
  if (entry && typeof entry === 'object') {
    const raw = entry.date != null ? entry.date : entry.day;
    return String(raw == null ? '' : raw).slice(0, 10);
  }
  return String(entry).slice(0, 10);
}

function isHoliday(holidays, dateStr) {
  if (!Array.isArray(holidays) || holidays.length === 0) return false;
  const d = dateStr || new Date().toISOString().slice(0, 10);
  return holidays.some((h) => holidayDate(h) === d);
}

/**
 * @param {object} tenant - Tenant row with timezone, business_hours_json
 * @param {Date} [now]
 * @returns {boolean} true if currently within business hours
 */
export function isWithinBusinessHours(tenant, now = new Date()) {
  if (!tenant) return true;
  let config = {};
  try {
    const raw = tenant.business_hours_json;
    if (raw && typeof raw === 'string') config = JSON.parse(raw);
    else if (raw && typeof raw === 'object') config = raw;
  } catch (e) {
    return true;
  }
  const tz = tenant.timezone || config.timezone || 'UTC';
  const clock = zonedClock(now, tz);
  if (isHoliday(config.holidays || [], clock.dateStr)) return false;
  const weekly = config.weekly || config.hours;
  if (!weekly || typeof weekly !== 'object') return true;
  const dayKey = DAY_KEYS[clock.weekday];
  const dayHours = weekly[dayKey] || weekly[dayKey.toUpperCase()];
  if (!dayHours) return false;
  const start = parseTime(dayHours.start || dayHours.open);
  const end = parseTime(dayHours.end || dayHours.close);
  if (start == null || end == null) return true;
  const min = clock.minutesSinceMidnight;
  if (start <= end) return min >= start && min < end;
  return min >= start || min < end;
}

/**
 * @param {object} tenant - Tenant row
 * @returns {'voicemail'|'callback'|'message'} after-hours behavior
 */
export function getAfterHoursBehavior(tenant) {
  const v = (tenant && tenant.after_hours_behavior) || 'voicemail';
  if (v === 'callback' || v === 'message') return v;
  return 'voicemail';
}

function normalizeState(state) {
  if (state == null) return null;
  const raw = String(state).trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return STATE_NAMES[raw.toLowerCase()] || null;
}

function nanpAreaCode(phone) {
  const n = normalizePhoneNumber(phone);
  if (!n || !n.startsWith('+1') || n.length < 5) return null;
  return n.slice(2, 5);
}

/** IANA zone for a US callee, from NANP area code, else lead state. Null if unknown. */
export function calleeTimeZone({ phone, state } = {}) {
  const npa = nanpAreaCode(phone);
  if (npa && NPA_TZ.has(npa)) return NPA_TZ.get(npa);
  const st = normalizeState(state);
  if (st && STATE_TZ[st]) return STATE_TZ[st];
  return null;
}

/** Federal TCPA: 8:00 inclusive through 21:00 exclusive, callee local time. Unknown zone is not callable. */
export function isWithinTcpaCallingWindow({ phone, state, now = new Date() } = {}) {
  const tz = calleeTimeZone({ phone, state });
  if (!tz) return false;
  const clock = zonedClock(now, tz);
  return clock.minutesSinceMidnight >= TCPA_START_MIN && clock.minutesSinceMidnight < TCPA_END_MIN;
}

/** Next 8:00am callee-local, or `now` when already inside the window. */
export function nextTcpaWindowStart({ phone, state, now = new Date() } = {}) {
  const tz = calleeTimeZone({ phone, state });
  if (!tz) return null;
  if (isWithinTcpaCallingWindow({ phone, state, now })) return new Date(now.getTime());
  for (let mins = 1; mins <= 36 * 60; mins += 1) {
    const candidate = new Date(now.getTime() + mins * 60 * 1000);
    const clock = zonedClock(candidate, tz);
    if (clock.hour === 8 && clock.minute === 0) return candidate;
  }
  return null;
}
